// src/components/LandingPage.js

import React, { useCallback, useState, useEffect } from 'react';
import uploadIcon from '../assets/upload-icon.svg';
import { useNavigate } from 'react-router-dom';
import * as SPLAT from 'gsplat';
import JSZip from 'jszip';
import { authFetch } from '../utils/apiHelper';
import { API_URL } from '../config';
const jsZip = new JSZip();
const scene = new SPLAT.Scene();

function xhrUpload(url, body, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', 'binary/octet-stream');
    xhr.setRequestHeader('Cache-Control', 'public, max-age=31536000, immutable');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr);
      else reject(new Error(`Upload failed: ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Upload network error'));
    xhr.send(body);
  });
}

const PLY_TYPE_SIZE = { float: 4, double: 8, int: 4, uint: 4, short: 2, ushort: 2, char: 1, uchar: 1 };

async function compressToCompactPLY(file) {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);

  // Find end_header
  const headerStr = new TextDecoder().decode(bytes.subarray(0, Math.min(65536, bytes.length)));
  const endToken = 'end_header\n';
  const headerEndIdx = headerStr.indexOf(endToken);
  if (headerEndIdx === -1) throw new Error('Invalid PLY header');
  const headerLength = headerEndIdx + endToken.length;

  // Parse properties
  const lines = headerStr.substring(0, headerEndIdx).split('\n');
  let vertexCount = 0;
  const properties = [];
  let inVertex = false;
  for (const line of lines) {
    const parts = line.trim().split(/\s+/);
    if (parts[0] === 'element') {
      inVertex = parts[1] === 'vertex';
      if (inVertex) vertexCount = parseInt(parts[2]);
    } else if (parts[0] === 'element' && parts[1] !== 'vertex') {
      inVertex = false;
    } else if (parts[0] === 'property' && inVertex) {
      properties.push({ name: parts[2], type: parts[1] });
    }
  }

  // Compute offsets
  let rowLength = 0;
  const propOffsets = {}, propTypes = {};
  for (const p of properties) {
    propOffsets[p.name] = rowLength;
    propTypes[p.name] = p.type;
    rowLength += PLY_TYPE_SIZE[p.type] ?? 4;
  }

  const dataView = new DataView(buffer, headerLength);
  const fRestNames = Array.from({ length: 45 }, (_, i) => `f_rest_${i}`)
    .filter(n => propOffsets[n] !== undefined);

  const getVal = (i, name) => {
    const base = i * rowLength + propOffsets[name];
    const t = propTypes[name];
    if (t === 'float') return dataView.getFloat32(base, true);
    if (t === 'double') return dataView.getFloat64(base, true);
    if (t === 'char') return dataView.getInt8(base);
    if (t === 'uchar') return dataView.getUint8(base);
    if (t === 'short') return dataView.getInt16(base, true);
    if (t === 'ushort') return dataView.getUint16(base, true);
    if (t === 'int') return dataView.getInt32(base, true);
    return 0;
  };

  // First pass: find max |f_rest| for scale factor
  let maxFRest = 1e-9;
  for (let i = 0; i < vertexCount; i++) {
    for (const name of fRestNames) {
      const v = Math.abs(getVal(i, name));
      if (v > maxFRest) maxFRest = v;
    }
  }
  const fRestScale = maxFRest / 127.0;

  // Build new PLY header (f_rest as char)
  const newPropLines = properties.map(p =>
    `property ${fRestNames.includes(p.name) ? 'char' : p.type} ${p.name}`
  ).join('\n');
  const newHeader = [
    'ply',
    'format binary_little_endian 1.0',
    `comment f_rest_scale ${fRestScale}`,
    `element vertex ${vertexCount}`,
    newPropLines,
    'end_header\n',
  ].join('\n');
  const headerBytes = new TextEncoder().encode(newHeader);

  // New row length
  let newRowLength = 0;
  for (const p of properties) {
    newRowLength += fRestNames.includes(p.name) ? 1 : (PLY_TYPE_SIZE[p.type] ?? 4);
  }

  // Second pass: write compact binary
  const output = new Uint8Array(headerBytes.length + vertexCount * newRowLength);
  output.set(headerBytes, 0);
  const outView = new DataView(output.buffer, headerBytes.length);
  let outOff = 0;
  for (let i = 0; i < vertexCount; i++) {
    for (const p of properties) {
      const val = getVal(i, p.name);
      if (fRestNames.includes(p.name)) {
        outView.setInt8(outOff, Math.max(-128, Math.min(127, Math.round(val / fRestScale))));
        outOff += 1;
      } else {
        const sz = PLY_TYPE_SIZE[p.type] ?? 4;
        if (p.type === 'float') outView.setFloat32(outOff, val, true);
        else if (p.type === 'double') outView.setFloat64(outOff, val, true);
        else if (p.type === 'int') outView.setInt32(outOff, val, true);
        else if (p.type === 'uint') outView.setUint32(outOff, val, true);
        else if (p.type === 'short') outView.setInt16(outOff, val, true);
        else if (p.type === 'ushort') outView.setUint16(outOff, val, true);
        else if (p.type === 'char') outView.setInt8(outOff, val);
        else if (p.type === 'uchar') outView.setUint8(outOff, val);
        else outView.setFloat32(outOff, val, true);
        outOff += sz;
      }
    }
  }

  // Gzip compress
  const cs = new CompressionStream('gzip');
  const writer = cs.writable.getWriter();
  writer.write(output);
  writer.close();
  const chunks = [];
  const reader = cs.readable.getReader();
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
  }
  return new Blob(chunks, { type: 'application/octet-stream' });
}

function compressToSOG(file, onStatus, onProgress) {
  return new Promise((resolve, reject) => {
    onStatus('SOG: reading file…');
    onProgress(null);

    file.arrayBuffer().then((plyBuffer) => {
      // eslint-disable-next-line no-undef
      const worker = new Worker(new URL('../sog-worker.js', import.meta.url));

      worker.onmessage = (e) => {
        const { type, text, progress, data, message } = e.data;
        if (type === 'log') {
          console.log('[SOG worker]', e.data.event);
        } else if (type === 'status') {
          if (text) onStatus(text);
          onProgress(progress);
        } else if (type === 'done') {
          worker.terminate();
          resolve(new Blob([data], { type: 'application/octet-stream' }));
        } else if (type === 'error') {
          worker.terminate();
          reject(new Error(message));
        }
      };

      worker.onerror = (err) => {
        worker.terminate();
        reject(err);
      };

      worker.postMessage({ plyBuffer }, [plyBuffer]);
    }).catch(reject);
  });
}

const LandingPage = () => {
  const navigate = useNavigate();
  const [dragging, setDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadProgress, setUploadProgress] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const elapsedRef = React.useRef(null);
  const [uploadFormat, setUploadFormat] = useState(() => localStorage.getItem('uploadFormat') || 'splat');
  let [file, setFile] = useState(null);

  // Initialize variables
  let modelId, zip;
  let biggestFile;
  let secondBiggestFile;
  let biggestFileSize = -1;
  let secondBiggestFileSize = -1;
  let biggestFilePath;
  let secondBiggestFilePath;

  const handleFormatChange = useCallback((format) => {
    setUploadFormat(format);
    localStorage.setItem('uploadFormat', format);
  }, []);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragging(false);
  }, []);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    setDragging(false);
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      setFile(event.dataTransfer.files[0]);
    }
  }, []);

  const handleChange = useCallback((event) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
    }
  }, []);

  const handleUpload = useCallback(async() => {
    if (!file) {
      console.error("No file selected for upload.");
      return;
    }

    if (file.name.endsWith('.ply') || file.name.endsWith('.splat') || file.name.endsWith('.zip') || file.name.endsWith('.sog')) {
    } else {
      console.error('Only *.ply, *.splat, *.sog or zip files are accepted');
      return;
    }

    setIsUploading(true);
    setUploadProgress(null);

    const usePLY = uploadFormat === 'ply';
    const useCPLY = uploadFormat === 'cply';
    const useSOG = uploadFormat === 'sog';

    if (file.name.endsWith('.ply')) {
      if (useCPLY) {
        setUploadStatus('Compressing to Compact PLY...');
        biggestFile = await compressToCompactPLY(file);
      } else if (useSOG) {
        setElapsedSec(0);
        elapsedRef.current = setInterval(() => setElapsedSec(s => s + 1), 1000);
        try { biggestFile = await compressToSOG(file, setUploadStatus, setUploadProgress); }
        finally { clearInterval(elapsedRef.current); elapsedRef.current = null; }
      } else if (usePLY) {
        biggestFile = file;
      } else {
        setUploadStatus('Converting to Splat...');
        await SPLAT.PLYLoader.LoadFromFileAsync(file, scene);
        biggestFile = scene.saveToFile();
      }
    } else if (file.name.endsWith('.zip')) {
      biggestFile = null;
      secondBiggestFile = null;
      biggestFileSize = -1;
      secondBiggestFileSize = -1;
      biggestFilePath = null;
      secondBiggestFilePath = null;

      zip = true;
      setUploadStatus('Extracting ZIP...');
      const zipContent = await jsZip.loadAsync(file);
      const promises = [];
      zipContent.forEach(async (relativePath, zipEntry) => {
        const promise = new Promise(async (resolve) => {
          const extractedFile = await zipEntry.async('blob');
          if (extractedFile && extractedFile.size > 1000) {
            const fileSize = extractedFile.size;
            if (fileSize > biggestFileSize) {
              secondBiggestFile = biggestFile;
              secondBiggestFileSize = biggestFileSize;
              secondBiggestFilePath = biggestFilePath;
              biggestFile = extractedFile;
              biggestFileSize = fileSize;
              biggestFilePath = relativePath;
            } else if (fileSize > secondBiggestFileSize) {
              secondBiggestFile = extractedFile;
              secondBiggestFileSize = fileSize;
              secondBiggestFilePath = relativePath;
            }
          }
          resolve();
        });
        promises.push(promise);
      });

      await Promise.all(promises);

      if (biggestFile && secondBiggestFile &&
        (biggestFilePath.endsWith('.ply') || biggestFilePath.endsWith('.splat')) &&
        (secondBiggestFilePath.endsWith('.ply'))) {

        if (biggestFilePath.endsWith('.ply')) {
          if (useCPLY) {
            setUploadStatus('Compressing to Compact PLY...');
            biggestFile = await compressToCompactPLY(biggestFile);
          } else if (useSOG) {
            setElapsedSec(0);
            elapsedRef.current = setInterval(() => setElapsedSec(s => s + 1), 1000);
            try { biggestFile = await compressToSOG(biggestFile, setUploadStatus, setUploadProgress); }
            finally { clearInterval(elapsedRef.current); elapsedRef.current = null; }
          } else if (!usePLY) {
            setUploadStatus('Converting to Splat...');
            await SPLAT.PLYLoader.LoadFromFileAsync(biggestFile, scene);
            biggestFile = scene.saveToFile();
          }
        }
      } else {
        setIsUploading(false);
        return;
      }
    } else {
      // .splat or .sog: upload directly as-is
      biggestFile = file;
    }

    // Determine upload extension for the main file
    const mainExtension = file.name.endsWith('.sog') ? 'sog'
      : useCPLY && !file.name.endsWith('.splat') ? 'cply'
      : useSOG && !file.name.endsWith('.splat') ? 'sog'
      : (!usePLY || file.name.endsWith('.splat')) ? 'splat' : 'ply';

    try {
      let response = await authFetch(`${API_URL}?extension=${mainExtension}`, {
        method: 'GET',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch presigned URL for upload');
      }

      let data = await response.json();
      let presignedUrl = data.url;
      modelId = data.id;

      setUploadStatus('Uploading...');
      setUploadProgress(0);
      await xhrUpload(presignedUrl, biggestFile, setUploadProgress);

      const formatParam = mainExtension === 'ply' ? '&format=ply' : mainExtension === 'cply' ? '&format=cply' : mainExtension === 'sog' ? '&format=sog' : '';

      if (!zip) {
        navigate(`/scene?modelId=${modelId}${formatParam}&npl=true`);
      } else {
        response = await authFetch(`${API_URL}?extension=ply&id=${modelId}_point_cloud`, {
          method: 'GET',
        });
        if (!response.ok) {
          throw new Error('Failed to fetch presigned URL for point cloud upload');
        }

        data = await response.json();
        presignedUrl = data.url;
        setUploadStatus('Uploading point cloud...');
        setUploadProgress(0);
        await xhrUpload(presignedUrl, secondBiggestFile, setUploadProgress);
        navigate(`/scene?modelId=${modelId}${formatParam}`);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      //setIsUploading(false);
    }
  }, [file, navigate, uploadFormat]);

  useEffect(() => {
    if (file) {
      handleUpload();
    }
  }, [file, handleUpload]);

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: dragging ? '#7A7A7A' : 'transparent',
        cursor: 'pointer',
        color: 'white',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      {isUploading ? (
        <div style={{ textAlign: 'center', userSelect: 'none' }}>
          <style>{`
            @keyframes cs-indeterminate {
              0%   { transform: translateX(-100%); }
              100% { transform: translateX(350%); }
            }
          `}</style>
          <p style={{ marginBottom: 16, fontSize: 14, color: '#ccc' }}>{uploadStatus || 'Please wait...'}</p>
          <div style={{ width: 260, height: 4, background: '#2a2a2a', borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
            {uploadProgress === null ? (
              <div style={{
                position: 'absolute', left: 0, top: 0,
                width: '40%', height: '100%',
                background: '#4a90d9', borderRadius: 2,
                animation: 'cs-indeterminate 1.2s ease-in-out infinite',
              }} />
            ) : (
              <div style={{
                width: `${uploadProgress}%`, height: '100%',
                background: '#4a90d9', borderRadius: 2,
                transition: 'width 0.15s ease-out',
              }} />
            )}
          </div>
          <p style={{ marginTop: 10, fontSize: 13, color: '#888' }}>
            {uploadProgress !== null ? `${uploadProgress}%` : ''}
            {elapsedSec > 0 ? `  ${Math.floor(elapsedSec / 60)}m ${String(elapsedSec % 60).padStart(2, '0')}s` : ''}
          </p>
        </div>
      ) : (
        <>
          <img src={uploadIcon} alt="Upload" style={{ width: 50, height: 50, filter: 'invert(100%)' }} />
          <p>Click here or drag a ply, splat, sog</p>
          <p>or zip file to upload</p>

          {/* Format toggle */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 16,
              position: 'relative',
              zIndex: 10,
            }}
          >
            <span style={{ fontSize: 13, color: '#aaa' }}>Upload as:</span>
            <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #555' }}>
              {[
                { id: 'splat', label: 'Splat' },
                { id: 'cply', label: 'Compact PLY' },
                { id: 'ply', label: 'PLY (full)' },
                { id: 'sog', label: 'SOG' },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => handleFormatChange(id)}
                  style={{
                    padding: '5px 14px',
                    fontSize: 13,
                    fontWeight: uploadFormat === id ? 600 : 400,
                    background: uploadFormat === id ? '#4a90d9' : 'transparent',
                    color: uploadFormat === id ? '#fff' : '#aaa',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {uploadFormat === 'cply' && (
            <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
              Keeps all spherical harmonics · ~4–6× smaller than PLY
            </p>
          )}
          {uploadFormat === 'ply' && (
            <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
              Full quality · preserves all data · largest file
            </p>
          )}
          {uploadFormat === 'sog' && (
            <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
              PlayCanvas SOG · keeps all spherical harmonics · ~15–20× smaller than PLY · slow to encode
            </p>
          )}

          <input
            type="file"
            onChange={handleChange}
            style={{ display: 'none' }}
            accept=".ply,.splat,.sog,.zip"
            id="fileInput"
          />
          <label htmlFor="fileInput" style={{ position: 'absolute', width: '100%', height: '100%', top: 0, left: 0 }}></label>
        </>
      )}
    </div>
  );
};

export default LandingPage;
